import {
  BarChart3,
  Book,
  Building,
  Building2,
  Calendar,
  CheckSquare,
  DollarSign,
  FileText,
  TrendingUp,
  UsersRound,
} from 'lucide-react';

import AdminBookingsTab from '@/components/v2/organisms/admin/tabs/AdminBookingsTab/AdminBookingsTab';
import AdminCalendarTab from '@/components/v2/organisms/admin/tabs/AdminCalendarTab/AdminCalendarTab';
import AdminDashboardTab from '@/components/v2/organisms/admin/tabs/AdminDashboardTab/AdminDashboardTab';
import AdminExpensesTab from '@/components/v2/organisms/admin/tabs/AdminExpensesTab/AdminExpensesTab';
import BusinessDashboardTab from '@/components/v2/organisms/admin/tabs/BusinessDashboardTab/BusinessDashboardTab';
import AdminGuestsTab from '@/components/v2/organisms/admin/tabs/AdminGuestsTab/AdminGuestsTab';
import AdminIncomeTab from '@/components/v2/organisms/admin/tabs/AdminIncomeTab/AdminIncomeTab';
import AdminPropertiesTab from '@/components/v2/organisms/admin/tabs/AdminPropertiesTab/AdminPropertiesTab';
import BusinessExpensesTab from '@/components/v2/organisms/admin/tabs/BusinessExpensesTab/BusinessExpensesTab';
import BusinessIncomeTab from '@/components/v2/organisms/admin/tabs/BusinessIncomeTab/BusinessIncomeTab';
import UserManagementTabs from '@/components/v2/organisms/admin/tabs/UserManagementTabs/UserManagementTabs';
import ContractsTab from '@/components/v2/organisms/admin/tabs/ContractsTab/ContractsTab';
import AdminTodosTab from '@/components/v2/organisms/admin/tabs/AdminTodosTab/AdminTodosTab';
import OrganizationsTab from '@/components/v2/organisms/admin/tabs/OrganizationsTab/OrganizationsTab';
import { Role } from './rolePermissions';

export interface AdminTab {
  key: string;
  label: string;
  description: string;
  component: React.ComponentType;
  icon: JSX.Element;
  group: 'property' | 'business';
}

export const adminTabs: AdminTab[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    description: 'Resumen general de tu operación',
    component: AdminDashboardTab,
    icon: <BarChart3 size={20} className="text-brand-primary" />,
    group: 'property',
  },
  {
    key: 'todos',
    label: 'Tareas',
    description: 'Lista de tareas pendientes y seguimiento de actividades',
    component: AdminTodosTab,
    icon: <CheckSquare size={20} className="text-brand-primary" />,
    group: 'property',
  },
  {
    key: 'calendar',
    label: 'Calendario',
    description: 'Disponibilidad y reservas',
    component: AdminCalendarTab,
    icon: <Calendar size={20} className="text-brand-primary" />,
    group: 'property',
  },
  {
    key: 'income',
    label: 'Ingresos',
    description: 'Control de cobros y pagos recibidos',
    component: AdminIncomeTab,
    icon: <TrendingUp size={20} className="text-brand-primary" />,
    group: 'property',
  },
  {
    key: 'expenses',
    label: 'Gastos',
    description: 'Gastos asociados a propiedades',
    component: AdminExpensesTab,
    icon: <DollarSign size={20} className="text-brand-primary" />,
    group: 'property',
  },
  {
    key: 'guests',
    label: 'Huéspedes',
    description: 'Información de los huéspedes',
    component: AdminGuestsTab,
    icon: <UsersRound size={20} className="text-brand-primary" />,
    group: 'property',
  },
  {
    key: 'bookings',
    label: 'Reservas',
    description: 'Gestión de todas las reservas',
    component: AdminBookingsTab,
    icon: <Book size={20} className="text-brand-primary" />,
    group: 'property',
  },
  {
    key: 'business-dashboard',
    label: 'Dashboard',
    description: 'Métricas e indicadores financieros del negocio',
    component: BusinessDashboardTab,
    icon: <BarChart3 size={20} className="text-brand-primary" />,
    group: 'business',
  },
  {
    key: 'business-income',
    label: 'Ingresos del negocio',
    description: 'Ingresos no asociados a propiedades específicas',
    component: BusinessIncomeTab,
    icon: <TrendingUp size={20} className="text-brand-primary" />,
    group: 'business',
  },
  {
    key: 'business-expenses',
    label: 'Gastos del negocio',
    description: 'Gastos generales del negocio',
    component: BusinessExpensesTab,
    icon: <DollarSign size={20} className="text-brand-primary" />,
    group: 'business',
  },
  {
    key: 'properties',
    label: 'Propiedades',
    description: 'Listado y configuración de propiedades',
    component: AdminPropertiesTab,
    icon: <Building2 size={20} className="text-brand-primary" />,
    group: 'business',
  },
  {
    key: 'users',
    label: 'Usuarios',
    description: 'Gestión de usuarios del sistema',
    component: UserManagementTabs,
    icon: <UsersRound size={20} className="text-brand-primary" />,
    group: 'business',
  },
  {
    key: 'contracts',
    label: 'Contratos',
    description: 'Gestionar contratos de administración de propiedades',
    component: ContractsTab,
    icon: <FileText size={20} className="text-brand-primary" />,
    group: 'business',
  },
  {
    key: 'organizations',
    label: 'Organizaciones',
    description: 'Gestionar organizaciones para clasificar propiedades',
    component: OrganizationsTab,
    icon: <Building size={20} className="text-brand-primary" />,
    group: 'business',
  },
];

export const propertyTabKeys = adminTabs
  .filter((tab) => tab.group === 'property')
  .map((tab) => tab.key);

export const businessTabKeys = adminTabs
  .filter((tab) => tab.group === 'business')
  .map((tab) => tab.key);

export const visibleTabsByRole: Record<Role, string[]> = {
  property_owner: ['dashboard', 'calendar', 'income', 'expenses'],
  property_manager: [
    'dashboard',
    'calendar',
    'bookings',
    'income',
    'expenses',
    'guests',
    'properties',
    'contracts',
    'organizations',
  ],
  cleaning: ['todos'],
  super_admin: adminTabs.map((tab) => tab.key),
};
